// src/components/features/analysis/IntelligenceTab.jsx
import React, { useState, useEffect } from 'react';
import { apiService } from '../../../services/api';
import {
  IntelligenceTable,
  IntelligenceTableHeader,
  IntelligenceTableCell,
  MethodsTabList,
  MethodsTabButton,
  SectionButtons,
  SectionButton,
  Section,
  Tag,
  CodeButton,
  IntelligenceButton,
  MaliciousCount,
  SignatureBlock,
  SignatureTableCell,
  SignatureTableHeader,
  ResponsiveIntelligenceTable,
  Base64TableCell,
  TruncatedText,
  ExpandButton
} from '../../../styles/AnalysisStyles';
import { usePivot } from '../../../hooks/usePivot';
import PivotResults from './PivotResults';

const IntelligenceTab = ({ analysisData, onCodeView }) => {
  const [activeSection, setActiveSection] = useState('code');
  const [activeMethodsTab, setActiveMethodsTab] = useState('methods');
  const [activePatternTab, setActivePatternTab] = useState('ip');
  const [activeFileTab, setActiveFileTab] = useState('signature');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const { pivotData, loading, error, pivotByIconHash, pivotByCertificate, pivotByPermHash, pivotByMalwareFamily, pivotByService, pivotByReceiver, pivotByIntentAction, clearPivot } = usePivot();
  const [showPivotResults, setShowPivotResults] = useState(false);
  const [componentCounts, setComponentCounts] = useState(null);

  useEffect(() => {
    if (!analysisData?.sha256) return;
    apiService.getComponentCounts(analysisData.sha256)
      .then(data => setComponentCounts(data))
      .catch(() => setComponentCounts({ services: {}, receivers: {}, intent_actions: {} }));
  }, [analysisData?.sha256]);

  // Helper function to truncate text and add line breaks
  const formatLongText = (text, maxLength = 100, rowIndex = null) => {
    if (!text) return 'N/A';
    
    const isExpanded = expandedRows.has(rowIndex);
    const needsTruncation = text.length > maxLength;
    
    if (!needsTruncation) {
      // For shorter text, just add line breaks for better readability
      return text.replace(/(.{50})/g, '$1\n');
    }
    
    if (isExpanded) {
      // Show full text with line breaks every 50 characters
      return text.replace(/(.{50})/g, '$1\n');
    } else {
      // Show truncated text with line breaks
      const truncated = text.substring(0, maxLength);
      return truncated.replace(/(.{50})/g, '$1\n') + '...';
    }
  };

  const toggleRowExpansion = (rowIndex) => {
    const newExpandedRows = new Set(expandedRows);
    if (expandedRows.has(rowIndex)) {
      newExpandedRows.delete(rowIndex);
    } else {
      newExpandedRows.add(rowIndex);
    }
    setExpandedRows(newExpandedRows);
  };

  const renderMethodsTable = () => {
    switch (activeMethodsTab) {
      case 'methods':
        return (
          <IntelligenceTable>
            <thead>
              <tr>
                <IntelligenceTableHeader>Method</IntelligenceTableHeader>
                <IntelligenceTableHeader>Tags</IntelligenceTableHeader>
                <IntelligenceTableHeader>Class</IntelligenceTableHeader>
                <IntelligenceTableHeader>Called By</IntelligenceTableHeader>
              </tr>
            </thead>
            <tbody>
              {Object.entries(analysisData?.intelligence?.tagged_methods || {}).map(([method, info]) => {
                // Support both v3 shape {tags, callers, class} and old shape (array)
                const tags = Array.isArray(info) ? info : (info.tags || []);
                const callers = Array.isArray(info) ? [] : (info.callers || []);
                return (
                  <tr key={method}>
                    <IntelligenceTableCell>{method}</IntelligenceTableCell>
                    <IntelligenceTableCell>
                      {tags.map(tag => (
                        <Tag key={tag} type={tag}>{tag}</Tag>
                      ))}
                    </IntelligenceTableCell>
                    <IntelligenceTableCell>{info.class}</IntelligenceTableCell>
                    <IntelligenceTableCell>
                      {callers.map((c, i) => (
                        <div key={i} style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#6b7280', marginBottom: '2px' }}>
                          {c.split('->')[1] || c}
                        </div>
                      ))}
                    </IntelligenceTableCell>
                  </tr>
                );
              })}
            </tbody>
          </IntelligenceTable>
        );

      case 'fields':
        return (
          <IntelligenceTable>
            <thead>
              <tr>
                <IntelligenceTableHeader>Class</IntelligenceTableHeader>
                <IntelligenceTableHeader>Field Name</IntelligenceTableHeader>
                <IntelligenceTableHeader>Value</IntelligenceTableHeader>
              </tr>
            </thead>
            <tbody>
              {analysisData?.intelligence?.suspicious_fields?.map((field, index) => (
                <tr key={index}>
                  <IntelligenceTableCell>{field.class}</IntelligenceTableCell>
                  <IntelligenceTableCell>{field.field_name}</IntelligenceTableCell>
                  <IntelligenceTableCell>{field.value || 'N/A'}</IntelligenceTableCell>
                </tr>
              ))}
            </tbody>
          </IntelligenceTable>
        );

      case 'predictions':
        return (
          <IntelligenceTable>
            <thead>
              <tr>
                <IntelligenceTableHeader>Subject</IntelligenceTableHeader>
                <IntelligenceTableHeader>Prediction</IntelligenceTableHeader>
                <IntelligenceTableHeader>Confidence</IntelligenceTableHeader>
              </tr>
            </thead>
            <tbody>
              {analysisData?.intelligence?.predictions?.length > 0 ? (
                analysisData.intelligence.predictions.map((prediction, index) => {
                  // Parse the prediction string format: "('IByN.json', 'Second stage', '100%')"
                  const match = prediction.match(/\('([^']+)', '([^']+)', '([^']+)'\)/);
                  if (!match) return null;
                  
                  const [_, feature, predictionValue, confidence] = match;
                  
                  return (
                    <tr key={index}>
                      <IntelligenceTableCell>{feature}</IntelligenceTableCell>
                      <IntelligenceTableCell>{predictionValue}</IntelligenceTableCell>
                      <IntelligenceTableCell>{confidence}</IntelligenceTableCell>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <IntelligenceTableCell colSpan="3">No predictions available</IntelligenceTableCell>
                </tr>
              )}
            </tbody>
          </IntelligenceTable>
        );

      default:
        return null;
    }
  };

  const renderPatternTables = () => {
    switch (activePatternTab) {
      case 'ip':
        return (
          <IntelligenceTable>
            <thead>
              <tr>
                <IntelligenceTableHeader>IP Address</IntelligenceTableHeader>
              </tr>
            </thead>
            <tbody>
              {analysisData?.intelligence?.malscanner?.ip_addresses.length > 0 ? (
                analysisData.intelligence.malscanner.ip_addresses.map((ip, index) => (
                  <tr key={index}>
                    <IntelligenceTableCell>{ip}</IntelligenceTableCell>
                  </tr>
                ))
              ) : (
                <tr>
                  <IntelligenceTableCell>No IP addresses found</IntelligenceTableCell>
                </tr>
              )}
            </tbody>
          </IntelligenceTable>
        );

      case 'domain':
        return (
          <IntelligenceTable>
            <thead>
              <tr>
                <IntelligenceTableHeader>Domain(s)</IntelligenceTableHeader>
              </tr>
            </thead>
            <tbody>
              {analysisData?.intelligence?.malscanner?.domains.map((domain, index) => (
                <tr key={index}>
                  <IntelligenceTableCell>{domain}</IntelligenceTableCell>
                </tr>
              ))}
            </tbody>
          </IntelligenceTable>
        );

      case 'strings':
        return (
          <IntelligenceTable>
            <thead>
              <tr>
                <IntelligenceTableHeader>Name</IntelligenceTableHeader>
                <IntelligenceTableHeader>Code</IntelligenceTableHeader>
              </tr>
            </thead>
            <tbody>
              {analysisData?.intelligence?.malscanner?.interesting_strings.map((stringData, index) => {
                const match = stringData.match(/\('([^']+)', \[(.*)\]\)/s);
                if (!match) return null;
                
                const name = match[1];
                const code = match[2]
                  .trim()
                  .replace(/^'|'$/g, '') // Remove surrounding quotes
                  .replace(/\\n/g, '\n')  // Replace \n with actual newlines
                  .replace(/\\'/g, "'");   // Replace \' with '
                
                return (
                  <tr key={index}>
                    <IntelligenceTableCell>{name}</IntelligenceTableCell>
                    <IntelligenceTableCell>
                      <CodeButton
                        disabled={!code}
                        onClick={() => code && onCodeView(code)}
                      >
                        {code ? 'View Code' : 'No Code Available'}
                      </CodeButton>
                    </IntelligenceTableCell>
                  </tr>
                );
              })}
            </tbody>
          </IntelligenceTable>
        );

            case 'base64':
        return (
          <ResponsiveIntelligenceTable>
            <thead>
              <tr>
                <IntelligenceTableHeader style={{ width: '50%' }}>Encoded String</IntelligenceTableHeader>
                <IntelligenceTableHeader style={{ width: '50%' }}>Decoded String</IntelligenceTableHeader>
              </tr>
            </thead>
            <tbody>
              {analysisData?.intelligence?.malscanner?.base64_decoded_strings.map((str, index) => {
                const matches = str.match(/\('([^']+)', '([^']+)'\)/);
                const encodedString = matches ? matches[1] : str;
                const decodedString = matches ? matches[2] : 'N/A';
                
                const isEncodedExpanded = expandedRows.has(`encoded-${index}`);
                const isDecodedExpanded = expandedRows.has(`decoded-${index}`);
                
                return (
                  <tr key={index}>
                    <Base64TableCell>
                      <TruncatedText>
                        {formatLongText(encodedString, 100, `encoded-${index}`)}
                      </TruncatedText>
                      {encodedString.length > 100 && (
                        <ExpandButton onClick={() => toggleRowExpansion(`encoded-${index}`)}>
                          {isEncodedExpanded ? 'Show Less' : 'Show More'}
                        </ExpandButton>
                      )}
                    </Base64TableCell>
                    <Base64TableCell>
                      <TruncatedText>
                        {formatLongText(decodedString, 100, `decoded-${index}`)}
                      </TruncatedText>
                      {decodedString.length > 100 && (
                        <ExpandButton onClick={() => toggleRowExpansion(`decoded-${index}`)}>
                          {isDecodedExpanded ? 'Show Less' : 'Show More'}
                        </ExpandButton>
                      )}
                    </Base64TableCell>
                  </tr>
                );
              })}
            </tbody>
          </ResponsiveIntelligenceTable>
        );

      default:
        return null;
    }
  };

  const renderFileTables = () => {
    switch (activeFileTab) {
      case 'signature':
        return (
          <ResponsiveIntelligenceTable>
            <thead>
              <tr>
                <SignatureTableHeader className="filename-header">Filename</SignatureTableHeader>
                <SignatureTableHeader className="reason-header">Reason</SignatureTableHeader>
                <SignatureTableHeader className="signature-header">File Signature</SignatureTableHeader>
              </tr>
            </thead>
            <tbody>
              {analysisData?.intelligence?.malscanner?.signature_file_mismatch?.length > 0 ? (
                analysisData.intelligence.malscanner.signature_file_mismatch.map((mismatch, index) => {
                  let filename = 'Unknown';
                  let reason = 'Unknown';
                  let signature = 'No signature available';
                  
                  try {
                    if (typeof mismatch === 'string') {
                      // Parse string representation of Python tuple: "('filename', 'reason', b'bytes')"
                      // First, unescape doubled backslashes from JSON/string escaping
                      const unescapedString = mismatch.replace(/\\\\/g, '\\');
                      const cleanString = unescapedString.trim();
                      
                      // Match tuple format: ('filename', "reason", "b'...bytes...'")
                      const tupleMatch = cleanString.match(/^\(\s*'([^']+)',\s*"([^"]+)",\s*"(b'[^']*')"\s*\)$/);
                      
                      if (tupleMatch) {
                        filename = tupleMatch[1];
                        reason = tupleMatch[2].replace(/\\'/g, "'"); // Unescape single quotes in reason
                        let sigPart = tupleMatch[3].trim();
                        
                        // Handle Python bytes format: b'...'
                        if (sigPart.startsWith("b'") && sigPart.endsWith("'")) {
                          sigPart = sigPart.slice(2, -1);
                          
                          // Convert escape sequences to readable hex
                          signature = sigPart
                            .replace(/\\x([0-9a-fA-F]{2})/g, (match, hex) => hex.toUpperCase())
                            .replace(/\\n/g, '\\n')
                            .replace(/\\r/g, '\\r')
                            .replace(/\\t/g, '\\t');
                          
                          // Format as hex bytes with spacing
                          const hexMatches = signature.match(/[0-9A-F]{2}/g);
                          if (hexMatches && hexMatches.length > 0) {
                            signature = hexMatches.join(' ');
                          }
                        }
                      } else {
                        // Fallback: try to extract from list format too
                        const listMatch = cleanString.match(/^\[\s*'([^']+)',\s*'([^']+)',\s*(b'[^']*')\s*\]$/);
                        if (listMatch) {
                          filename = listMatch[1];
                          reason = listMatch[2];
                          let sigPart = listMatch[3].trim();
                          
                          if (sigPart.startsWith("b'") && sigPart.endsWith("'")) {
                            sigPart = sigPart.slice(2, -1);
                            signature = sigPart
                              .replace(/\\x([0-9a-fA-F]{2})/g, (match, hex) => hex.toUpperCase())
                              .replace(/\\n/g, '\\n')
                              .replace(/\\r/g, '\\r')
                              .replace(/\\t/g, '\\t');
                            
                            const hexMatches = signature.match(/[0-9A-F]{2}/g);
                            if (hexMatches && hexMatches.length > 0) {
                              signature = hexMatches.join(' ');
                            }
                          }
                        }
                      }
                    } else if (Array.isArray(mismatch)) {
                      // Handle actual array format
                      filename = mismatch[0] || 'Unknown';
                      reason = mismatch[1] || 'Unknown';
                      if (mismatch[2]) {
                        // Convert bytes to hex if it's a buffer/array
                        if (typeof mismatch[2] === 'object') {
                          signature = Array.from(mismatch[2])
                            .map(byte => byte.toString(16).padStart(2, '0').toUpperCase())
                            .join(' ');
                        } else {
                          signature = String(mismatch[2]);
                        }
                      }
                    }
                  } catch (error) {
                    console.error('Error parsing mismatch data:', error, mismatch);
                    filename = `Parse error for item ${index}`;
                    reason = 'Could not parse data';
                    signature = 'Parse error';
                  }

                  return (
                    <tr key={index}>
                      <SignatureTableCell className="filename-cell">
                        {filename}
                      </SignatureTableCell>
                      <SignatureTableCell className="reason-cell">
                        {reason}
                      </SignatureTableCell>
                      <SignatureTableCell className="signature-cell">
                        <SignatureBlock>{signature}</SignatureBlock>
                      </SignatureTableCell>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <SignatureTableCell colSpan="3">No signature mismatches found</SignatureTableCell>
                </tr>
              )}
            </tbody>
          </ResponsiveIntelligenceTable>
        );

      case 'files':
        return (
          <IntelligenceTable>
            <thead>
              <tr>
                <IntelligenceTableHeader>File Name</IntelligenceTableHeader>
              </tr>
            </thead>
            <tbody>
              {analysisData?.intelligence?.malscanner?.interesting_files.map((file, index) => (
                <tr key={index}>
                  <IntelligenceTableCell>{file}</IntelligenceTableCell>
                </tr>
              ))}
            </tbody>
          </IntelligenceTable>
        );
        
      case 'libraries':
        return (
          <IntelligenceTable>
            <thead>
              <tr>
                <IntelligenceTableHeader>Name</IntelligenceTableHeader>
                <IntelligenceTableHeader>File Path</IntelligenceTableHeader>
                <IntelligenceTableHeader>Architecture</IntelligenceTableHeader>
              </tr>
            </thead>
            <tbody>
              {analysisData?.intelligence?.malscanner?.native_libraries.map((library, index) => {
                const match = library.match(/\('([^']+)', '([^']+)', '([^']+)'\)/);
                if (!match) return null;
                
                const [_, name, filePath, architecture] = match;
                
                return (
                  <tr key={index}>
                    <IntelligenceTableCell>{name}</IntelligenceTableCell>
                    <IntelligenceTableCell>{filePath}</IntelligenceTableCell>
                    <IntelligenceTableCell>{architecture}</IntelligenceTableCell>
                  </tr>
                );
              })}
            </tbody>
          </IntelligenceTable>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <IntelligenceTable>
        <tbody>
          {/* VirusTotal Row */}
          <tr>
            <IntelligenceTableCell>VirusTotal</IntelligenceTableCell>
            <IntelligenceTableCell>
              {analysisData?.intelligence?.virustotal?.is_available === "True" && (
                <>
                  {analysisData?.intelligence?.virustotal?.malicious_count && (
                    <MaliciousCount>
                      {analysisData.intelligence.virustotal.malicious_count}/67
                    </MaliciousCount>
                  )}
                  <IntelligenceButton
                    type="virustotal"
                    onClick={() => window.open(`https://www.virustotal.com/gui/file/${analysisData.sha256}`, '_blank')}
                  >
                    VirusTotal
                  </IntelligenceButton>
                </>
              )}
              {analysisData?.intelligence?.virustotal?.is_available !== "True" && "Not Available"}
            </IntelligenceTableCell>
          </tr>

          {/* Play Store Row */}
          <tr>
            <IntelligenceTableCell>Play Store</IntelligenceTableCell>
            <IntelligenceTableCell>
              {analysisData?.intelligence?.playstore?.is_available === "True" ? (
                <IntelligenceButton
                  type="playstore"
                  onClick={() => window.open(`https://play.google.com/store/apps/details?id=${analysisData.package_name}`, '_blank')}
                >
                  Play Store
                </IntelligenceButton>
              ) : (
                "Not Available"
              )}
            </IntelligenceTableCell>
          </tr>
        </tbody>
      </IntelligenceTable>

      <SectionButtons>
        <SectionButton 
          active={activeSection === 'code'} 
          onClick={() => setActiveSection('code')}
        >
          Code Analysis
        </SectionButton>
        <SectionButton 
          active={activeSection === 'pattern'} 
          onClick={() => setActiveSection('pattern')}
        >
          Pattern Search
        </SectionButton>
        <SectionButton 
          active={activeSection === 'file'} 
          onClick={() => setActiveSection('file')}
        >
          File Analysis
        </SectionButton>
        <SectionButton
          active={activeSection === 'targets'}
          onClick={() => setActiveSection('targets')}
        >
          Target Packages
        </SectionButton>
        <SectionButton
          active={activeSection === 'components'}
          onClick={() => setActiveSection('components')}
        >
          Components
        </SectionButton>
        <SectionButton
          active={activeSection === 'pivots'}
          onClick={() => setActiveSection('pivots')}
        >
          Pivoting
        </SectionButton>
      </SectionButtons>

      <Section visible={activeSection === 'code'}>
        <MethodsTabList>
          <MethodsTabButton 
            active={activeMethodsTab === 'methods'} 
            onClick={() => setActiveMethodsTab('methods')}
          >
            Methods
          </MethodsTabButton>
          <MethodsTabButton 
            active={activeMethodsTab === 'fields'} 
            onClick={() => setActiveMethodsTab('fields')}
          >
            Class Fields
          </MethodsTabButton>
          <MethodsTabButton 
            active={activeMethodsTab === 'predictions'} 
            onClick={() => setActiveMethodsTab('predictions')}
          >
            Predictions
          </MethodsTabButton>
        </MethodsTabList>

        {renderMethodsTable()}
      </Section>

      <Section visible={activeSection === 'pattern'}>
        <MethodsTabList>
          <MethodsTabButton 
            active={activePatternTab === 'ip'} 
            onClick={() => setActivePatternTab('ip')}
          >
            IP Addresses
          </MethodsTabButton>

          <MethodsTabButton 
            active={activePatternTab === 'domain'} 
            onClick={() => setActivePatternTab('domain')}
          >
            Domain(s)
          </MethodsTabButton>

          <MethodsTabButton 
            active={activePatternTab === 'strings'} 
            onClick={() => setActivePatternTab('strings')}
          >
            Interesting Strings
          </MethodsTabButton>

          <MethodsTabButton 
            active={activePatternTab === 'base64'} 
            onClick={() => setActivePatternTab('base64')}
          >
            Base64 Decoded Strings
          </MethodsTabButton>
        </MethodsTabList>

        {renderPatternTables()}
      </Section>

      <Section visible={activeSection === 'file'}>
        <MethodsTabList>
          <MethodsTabButton 
            active={activeFileTab === 'signature'} 
            onClick={() => setActiveFileTab('signature')}
          >
            File Signature Mismatch
          </MethodsTabButton>

          <MethodsTabButton 
            active={activeFileTab === 'files'} 
            onClick={() => setActiveFileTab('files')}
          >
            Interesting Files
          </MethodsTabButton>
          
          <MethodsTabButton 
            active={activeFileTab === 'libraries'} 
            onClick={() => setActiveFileTab('libraries')}
          >
            Native Libraries
          </MethodsTabButton>
        </MethodsTabList>

        {renderFileTables()}
      </Section>

      <Section visible={activeSection === 'targets'}>
        <IntelligenceTable>
          <thead>
            <tr>
              <IntelligenceTableHeader>Package Name</IntelligenceTableHeader>
            </tr>
          </thead>
          <tbody>
            {(analysisData?.intelligence?.malscanner?.target_packages || []).length > 0 ? (
              analysisData.intelligence.malscanner.target_packages.map((pkg, i) => (
                <tr key={i}>
                  <IntelligenceTableCell>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{pkg}</span>
                  </IntelligenceTableCell>
                </tr>
              ))
            ) : (
              <tr>
                <IntelligenceTableCell>No targeted packages identified</IntelligenceTableCell>
              </tr>
            )}
          </tbody>
        </IntelligenceTable>
      </Section>

      <Section visible={activeSection === 'components'}>
        {/* Services */}
        <div style={{ marginBottom: '1.5rem' }}>
          {(analysisData?.services || []).length > 0 ? (
            <IntelligenceTable>
              <thead>
                <tr>
                  <IntelligenceTableHeader>Service Name</IntelligenceTableHeader>
                  <IntelligenceTableHeader style={{ width: '100px' }}>Pivot</IntelligenceTableHeader>
                </tr>
              </thead>
              <tbody>
                {analysisData.services.map((svc, i) => {
                  const count = componentCounts?.services?.[svc] || 0;
                  return (
                    <tr key={i}>
                      <IntelligenceTableCell style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{svc}</IntelligenceTableCell>
                      <IntelligenceTableCell>
                        {count > 0 ? (
                          <IntelligenceButton type="pivot" onClick={async () => {
                            await pivotByService(svc, 100, analysisData.sha256);
                            setShowPivotResults(true);
                            setActiveSection('pivots');
                          }}>Pivot ({count})</IntelligenceButton>
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>unique</span>
                        )}
                      </IntelligenceTableCell>
                    </tr>
                  );
                })}
              </tbody>
            </IntelligenceTable>
          ) : (
            <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>No services declared</div>
          )}
        </div>

        {/* Receivers */}
        <div style={{ marginBottom: '1.5rem' }}>
          {(analysisData?.receivers || []).length > 0 ? (
            <IntelligenceTable>
              <thead>
                <tr>
                  <IntelligenceTableHeader>Receiver Name</IntelligenceTableHeader>
                  <IntelligenceTableHeader style={{ width: '100px' }}>Pivot</IntelligenceTableHeader>
                </tr>
              </thead>
              <tbody>
                {analysisData.receivers.map((rcv, i) => {
                  const count = componentCounts?.receivers?.[rcv] || 0;
                  return (
                    <tr key={i}>
                      <IntelligenceTableCell style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{rcv}</IntelligenceTableCell>
                      <IntelligenceTableCell>
                        {count > 0 ? (
                          <IntelligenceButton type="pivot" onClick={async () => {
                            await pivotByReceiver(rcv, 100, analysisData.sha256);
                            setShowPivotResults(true);
                            setActiveSection('pivots');
                          }}>Pivot ({count})</IntelligenceButton>
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>unique</span>
                        )}
                      </IntelligenceTableCell>
                    </tr>
                  );
                })}
              </tbody>
            </IntelligenceTable>
          ) : (
            <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>No broadcast receivers declared</div>
          )}
        </div>

      </Section>

      <Section visible={activeSection === 'pivots'}>
        <MethodsTabList>
          {analysisData?.icon_hash && (
            <MethodsTabButton 
              active={!showPivotResults}
              onClick={async () => {
                await pivotByIconHash(analysisData.icon_hash, 100);
                setShowPivotResults(true);
              }}
            >
               Icon Hash Pivot
            </MethodsTabButton>
          )}
          {analysisData?.certificates && analysisData.certificates.length > 0 && (
            <MethodsTabButton 
              active={!showPivotResults}
              onClick={async () => {
                const certSha256 = analysisData.certificates[0].sha256;
                await pivotByCertificate(certSha256, 100);
                setShowPivotResults(true);
              }}
            >
              Certificate Pivot
            </MethodsTabButton>
          )}
          {analysisData?.perm_hash && (
            <MethodsTabButton 
              active={!showPivotResults}
              onClick={async () => {
                await pivotByPermHash(analysisData.perm_hash, 100);
                setShowPivotResults(true);
              }}
            >
              Perm Hash Pivot
            </MethodsTabButton>
          )}
          {analysisData?.malware_family && analysisData.malware_family !== 'Unknown' && (
            <MethodsTabButton 
              active={!showPivotResults}
              onClick={async () => {
                await pivotByMalwareFamily(analysisData.malware_family, 100);
                setShowPivotResults(true);
              }}
            >
              Malware Family Pivot
            </MethodsTabButton>
          )}
          {showPivotResults && (
            <MethodsTabButton 
              active={true}
              onClick={() => {
                setShowPivotResults(false);
                clearPivot();
              }}
            >
              Close Results
            </MethodsTabButton>
          )}
        </MethodsTabList>

        {showPivotResults && (
          <PivotResults 
            pivotData={pivotData} 
            loading={loading} 
            error={error}
          />
        )}

        {!showPivotResults && (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
            <p>Select a pivot type above to find related APKs:</p>
            <ul style={{ textAlign: 'left', display: 'inline-block', fontSize: '0.9rem' }}>
              <li><strong>Icon Hash Pivot:</strong> Find all APKs with the same icon</li>
              <li><strong>Certificate Pivot:</strong> Find all APKs signed with the same certificate</li>
              <li><strong>Perm Hash Pivot:</strong> Find all APKs with identical permission sets</li>
              <li><strong>Malware Family Pivot:</strong> Find all variants in the same malware family</li>
              <li><strong>Service / Receiver / Intent Pivot:</strong> Use the Components tab to pivot on individual components</li>
            </ul>
          </div>
        )}
      </Section>
    </div>
  );
};

export default IntelligenceTab;